import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  CustodySchema,
  CustodyCreateInputSchema,
  CustodyUpdateInputSchema,
  type Custody,
  type CustodyCreateInput,
  type CustodyUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `custody` (prise en charge temporaire, org-wide en v0).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`CustodyCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { CustodyCreateInput };

/** Nom de la collection Firestore de la famille custody. */
export const CUSTODIES_COLLECTION = 'custodies';

/**
 * Repository typé pour la famille `custody`.
 * Toute lecture est validée par `CustodySchema`, toute écriture par
 * `CustodyCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface CustodyRepository {
  create(input: CustodyCreateInput): Promise<Custody>;
  list(options?: ListOptions): Promise<Custody[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Custody | null>;
  update(id: string, patch: CustodyUpdateInput): Promise<Custody>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Custody>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Custody>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Custody>;
}

const custodyConverter = createConverter<Custody>((raw: DocumentData) => CustodySchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createCustodyRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): CustodyRepository {
  const col = collection(db, CUSTODIES_COLLECTION).withConverter(custodyConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Custody>(db, col, CUSTODIES_COLLECTION, getActorId);

  return {
    async create(input: CustodyCreateInput): Promise<Custody> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = CustodyCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.itemAssetId !== undefined) optional['itemAssetId'] = fields.itemAssetId;
      if (fields.description !== undefined) optional['description'] = fields.description;
      if (fields.ownerContactId !== undefined) optional['ownerContactId'] = fields.ownerContactId;
      if (fields.receivedAt !== undefined) optional['receivedAt'] = fields.receivedAt;
      if (fields.dueBackAt !== undefined) optional['dueBackAt'] = fields.dueBackAt;
      if (fields.returnedAt !== undefined) optional['returnedAt'] = fields.returnedAt;
      if (fields.conditionInspectionIds !== undefined) optional['conditionInspectionIds'] = fields.conditionInspectionIds;
      if (fields.responsibleWorkerId !== undefined) optional['responsibleWorkerId'] = fields.responsibleWorkerId;
      if (fields.workOrderId !== undefined) optional['workOrderId'] = fields.workOrderId;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.incidentId !== undefined) optional['incidentId'] = fields.incidentId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Custody);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return CustodySchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Custody[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Custody | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: CustodyUpdateInput): Promise<Custody> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = CustodyUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, CUSTODIES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`custody ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
